import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Modal } from '@/components/ui/Modal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Badge } from '@/components/ui/Badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { Select } from '@/components/ui/Select';
import { api } from '@/lib/api';
import { toast } from 'react-hot-toast';
import type { PushTemplate, Company, ClienteFornecedor, SendPushRequest, SendPushResult } from '@/types';
import { PUSH_TEMPLATE_CATEGORY_LABELS } from '@/types';
import { Plus, Pencil, Trash2, Send, Smartphone } from 'lucide-react';

const PUSH_BASE = '/push-notifications';

export const PushNotificationsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('templates');
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<PushTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    title: '',
    body: '',
    category: 'general' as string,
    companyId: '' as string,
    isActive: true,
  });
  const [sendForm, setSendForm] = useState<SendPushRequest & { templateId: string }>({
    templateId: '',
    title: '',
    body: '',
    sendToAll: false,
    clienteFornecedorIds: [],
    companyIds: [],
  });
  const [companyFilter, setCompanyFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [isActiveFilter, setIsActiveFilter] = useState<string>('true');
  const [sending, setSending] = useState(false);

  const [clientFilters, setClientFilters] = useState({
    companyId: '' as string,
    marca: '',
    modelo: '',
    minTitulosAberto: '' as string,
    maxTitulosAberto: '' as string,
    minMesesLocacao: '' as string,
    maxMesesLocacao: '' as string,
  });
  const [clientNameSearch, setClientNameSearch] = useState('');

  const { data: templatesData, isLoading: loadingTemplates } = useQuery({
    queryKey: ['push-templates', companyFilter, categoryFilter, isActiveFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (companyFilter) params.set('companyId', companyFilter);
      if (categoryFilter) params.set('category', categoryFilter);
      if (isActiveFilter !== '') params.set('isActive', isActiveFilter);
      const res = await api.get(`${PUSH_BASE}/templates?${params}`);
      const body = res.data;
      return body;
    },
  });

  const { data: companiesData } = useQuery({
    queryKey: ['companies-push'],
    queryFn: async () => {
      const res = await api.get('/companies?isActive=true');
      return res.data;
    },
  });

  const { data: marcasData } = useQuery({
    queryKey: ['clientes-fornecedores-marcas', clientFilters.companyId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (clientFilters.companyId) params.set('companyId', clientFilters.companyId);
      const res = await api.get(`/clientes-fornecedores/marcas?${params}`);
      return res.data;
    },
  });
  const { data: modelosData } = useQuery({
    queryKey: ['clientes-fornecedores-modelos', clientFilters.marca, clientFilters.companyId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (clientFilters.marca) params.set('marca', clientFilters.marca);
      if (clientFilters.companyId) params.set('companyId', clientFilters.companyId);
      const res = await api.get(`/clientes-fornecedores/modelos?${params}`);
      return res.data;
    },
  });

  const clientFiltersKey = [
    clientFilters.companyId,
    clientFilters.marca,
    clientFilters.modelo,
    clientFilters.minTitulosAberto,
    clientFilters.maxTitulosAberto,
    clientFilters.minMesesLocacao,
    clientFilters.maxMesesLocacao,
  ];
  const { data: clientesData, isLoading: loadingClientes } = useQuery({
    queryKey: ['clientes-fornecedores-push', ...clientFiltersKey],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('limit', '2000');
      if (clientFilters.companyId) params.set('companyId', clientFilters.companyId);
      if (clientFilters.marca.trim()) params.set('marca', clientFilters.marca.trim());
      if (clientFilters.modelo.trim()) params.set('modelo', clientFilters.modelo.trim());
      if (clientFilters.minTitulosAberto !== '') params.set('minTitulosAberto', clientFilters.minTitulosAberto);
      if (clientFilters.maxTitulosAberto !== '') params.set('maxTitulosAberto', clientFilters.maxTitulosAberto);
      if (clientFilters.minMesesLocacao !== '') params.set('minMesesLocacao', clientFilters.minMesesLocacao);
      if (clientFilters.maxMesesLocacao !== '') params.set('maxMesesLocacao', clientFilters.maxMesesLocacao);
      const res = await api.get(`/clientes-fornecedores?${params}`);
      return res.data;
    },
  });

  const templates: PushTemplate[] = useMemo(() => {
    if (!templatesData) return [];
    if (Array.isArray(templatesData)) return templatesData as PushTemplate[];
    const data = (templatesData as { data?: unknown })?.data;
    return (Array.isArray(data) ? data : []) as PushTemplate[];
  }, [templatesData]);
  const companies: Company[] = useMemo(() => {
    const d = companiesData?.data;
    if (Array.isArray(d)) return d as Company[];
    if (d && typeof d === 'object' && 'companies' in d) return (d as { companies: Company[] }).companies;
    return [];
  }, [companiesData]);
  const clientes: ClienteFornecedor[] = useMemo(() => {
    const d = clientesData?.data ?? clientesData;
    if (Array.isArray(d)) return d as ClienteFornecedor[];
    if (d && typeof d === 'object' && Array.isArray((d as { items?: unknown }).items)) {
      return (d as { items: ClienteFornecedor[] }).items;
    }
    return [];
  }, [clientesData]);
  const clientesFiltered = useMemo(() => {
    const q = clientNameSearch.trim().toLowerCase();
    if (!q) return clientes;
    return clientes.filter(
      (c) =>
        (c.nome?.toLowerCase().includes(q) ?? false) ||
        (String(c.codigo ?? '').toLowerCase().includes(q))
    );
  }, [clientes, clientNameSearch]);
  const marcas: string[] = useMemo(() => {
    const d = marcasData?.data;
    return Array.isArray(d) ? d : [];
  }, [marcasData]);
  const modelos: string[] = useMemo(() => {
    const d = modelosData?.data;
    return Array.isArray(d) ? d : [];
  }, [modelosData]);

  const createTemplateMutation = useMutation({
    mutationFn: async (payload: typeof templateForm) => {
      await api.post(`${PUSH_BASE}/templates`, {
        name: payload.name,
        title: payload.title,
        body: payload.body,
        category: payload.category || 'general',
        companyId: payload.companyId || null,
        isActive: payload.isActive,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['push-templates'] });
      setTemplateModalOpen(false);
      resetTemplateForm();
      toast.success('Template criado com sucesso');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Erro ao criar template');
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: typeof templateForm }) => {
      await api.put(`${PUSH_BASE}/templates/${id}`, {
        name: payload.name,
        title: payload.title,
        body: payload.body,
        category: payload.category || 'general',
        companyId: payload.companyId || null,
        isActive: payload.isActive,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['push-templates'] });
      setTemplateModalOpen(false);
      setEditingTemplate(null);
      resetTemplateForm();
      toast.success('Template atualizado com sucesso');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Erro ao atualizar template');
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`${PUSH_BASE}/templates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['push-templates'] });
      toast.success('Template excluído');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Erro ao excluir template');
    },
  });

  function resetTemplateForm() {
    setTemplateForm({
      name: '',
      title: '',
      body: '',
      category: 'general',
      companyId: '',
      isActive: true,
    });
  }

  const openCreateTemplate = () => {
    setEditingTemplate(null);
    resetTemplateForm();
    setTemplateModalOpen(true);
  };

  const openEditTemplate = (t: PushTemplate) => {
    setEditingTemplate(t);
    setTemplateForm({
      name: t.name,
      title: t.title,
      body: t.body,
      category: t.category ?? 'general',
      companyId: t.companyId ?? '',
      isActive: t.isActive,
    });
    setTemplateModalOpen(true);
  };

  const handleSaveTemplate = () => {
    if (!templateForm.name.trim() || !templateForm.title.trim() || !templateForm.body.trim()) {
      toast.error('Nome, título e corpo são obrigatórios');
      return;
    }
    if (editingTemplate) {
      updateTemplateMutation.mutate({ id: editingTemplate.id, payload: templateForm });
    } else {
      createTemplateMutation.mutate(templateForm);
    }
  };

  const handleSendPush = async () => {
    const hasContent = sendForm.templateId || (sendForm.title?.trim() && sendForm.body?.trim());
    if (!hasContent) {
      toast.error('Selecione um template ou preencha título e corpo');
      return;
    }
    const hasRecipients =
      sendForm.sendToAll === true ||
      (sendForm.clienteFornecedorIds?.length ?? 0) > 0 ||
      (sendForm.companyIds?.length ?? 0) > 0;
    if (!hasRecipients) {
      toast.error('Marque "Enviar para todos" ou selecione ao menos um cliente ou uma empresa');
      return;
    }
    setSending(true);
    try {
      const res = await api.post<{ success: boolean; data: SendPushResult; message?: string }>(
        `${PUSH_BASE}/send`,
        {
          templateId: sendForm.templateId || undefined,
          title: sendForm.title || undefined,
          body: sendForm.body || undefined,
          sendToAll: sendForm.sendToAll === true ? true : undefined,
          clienteFornecedorIds: sendForm.clienteFornecedorIds?.length ? sendForm.clienteFornecedorIds : undefined,
          companyIds: sendForm.companyIds?.length ? sendForm.companyIds : undefined,
        }
      );
      const result = res.data.data;
      toast.success(
        `Enviado: ${result.successCount} sucesso, ${result.failedCount} falha(s). ${result.totalRecipients} destinatários, ${result.totalTokens} dispositivos.`
      );
      setSendForm((prev) => ({
        ...prev,
        title: '',
        body: '',
        sendToAll: false,
        clienteFornecedorIds: [],
        companyIds: [],
      }));
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erro ao enviar push');
    } finally {
      setSending(false);
    }
  };

  const toggleCliente = (id: string) => {
    setSendForm((prev) => {
      const ids = prev.clienteFornecedorIds ?? [];
      const set = new Set(ids);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...prev, clienteFornecedorIds: Array.from(set) };
    });
  };

  const toggleCompany = (id: string) => {
    setSendForm((prev) => {
      const ids = prev.companyIds ?? [];
      const set = new Set(ids);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...prev, companyIds: Array.from(set) };
    });
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="font-heading text-heading-2 font-semibold flex items-center gap-2">
          Notificações Push
        </h1>
        <p className="text-muted-foreground text-body-sm mt-1">
          Gerencie templates e envie alertas push para o aplicativo mobile (por clientes ou empresas).
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="send">Enviar</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Templates de push</CardTitle>
              <Button onClick={openCreateTemplate}>
                <Plus className="h-4 w-4 mr-2" />
                Novo template
              </Button>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 mb-4">
                <Select
                  value={companyFilter}
                  onValueChange={setCompanyFilter}
                  placeholder="Todas as empresas"
                >
                  <option value="">Todas</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
                <Select
                  value={categoryFilter}
                  onValueChange={setCategoryFilter}
                  placeholder="Todas as categorias"
                >
                  <option value="">Todas</option>
                  {Object.entries(PUSH_TEMPLATE_CATEGORY_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
                <Select
                  value={isActiveFilter}
                  onValueChange={setIsActiveFilter}
                  placeholder="Status"
                >
                  <option value="">Todos</option>
                  <option value="true">Ativos</option>
                  <option value="false">Inativos</option>
                </Select>
              </div>
              {loadingTemplates ? (
                <p className="text-muted-foreground">Carregando...</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Categoria</TableHead>
                      <TableHead>Título</TableHead>
                      <TableHead>Corpo (resumo)</TableHead>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[100px]">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {templates.map((t) => (
                      <TableRow key={t.id}>
                        <TableCell className="font-medium">{t.name}</TableCell>
                        <TableCell>{PUSH_TEMPLATE_CATEGORY_LABELS[t.category] ?? t.category}</TableCell>
                        <TableCell>{t.title}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{t.body}</TableCell>
                        <TableCell>{(t as any).company?.name ?? '-'}</TableCell>
                        <TableCell>
                          <Badge variant={t.isActive ? 'default' : 'secondary'}>
                            {t.isActive ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditTemplate(t)}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if (window.confirm('Excluir este template?')) {
                                  deleteTemplateMutation.mutate(t.id);
                                }
                              }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {!loadingTemplates && templates.length === 0 && (
                <p className="text-muted-foreground py-8 text-center">Nenhum template cadastrado.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="send" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Enviar notificação push</CardTitle>
              <p className="text-sm text-muted-foreground">
                Selecione um template (opcional) ou preencha título e corpo. Escolha clientes e/ou empresas como destinatários.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Template (opcional)</label>
                <Select
                  value={sendForm.templateId}
                  onValueChange={(v) => {
                    setSendForm((prev) => ({ ...prev, templateId: v }));
                    const t = templates.find((x) => x.id === v);
                    if (t) {
                      setSendForm((prev) => ({ ...prev, title: t.title, body: t.body }));
                    }
                  }}
                  placeholder="Nenhum"
                >
                  <option value="">Nenhum (usar título/corpo abaixo)</option>
                  {templates.filter((t) => t.isActive).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Título</label>
                <Input
                  value={sendForm.title}
                  onChange={(e) => setSendForm((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="Título da notificação (edite se não usar template)"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Corpo</label>
                <Textarea
                  value={sendForm.body}
                  onChange={(e) => setSendForm((prev) => ({ ...prev, body: e.target.value }))}
                  placeholder="Mensagem (edite se não usar template)"
                  rows={3}
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendForm.sendToAll === true}
                  onChange={(e) => setSendForm((prev) => ({ ...prev, sendToAll: e.target.checked }))}
                />
                <span className="text-sm font-medium">Enviar para todos os usuários do app</span>
              </label>
              <p className="text-xs text-muted-foreground -mt-2 ml-6">
                Use para avisar sobre nova versão (ex.: template &quot;Nova versão do app&quot;). Todos os usuários ativos do ZehMotoca receberão.
              </p>

              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium mb-2">Filtros para seleção de clientes {sendForm.sendToAll && '(ignorado quando &quot;Enviar para todos&quot; está marcado)'}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 flex-wrap">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-0.5">Empresa</label>
                      <Select
                        value={clientFilters.companyId}
                        onValueChange={(v) => setClientFilters((f) => ({ ...f, companyId: v }))}
                        placeholder="Todas"
                      >
                        <option value="">Todas</option>
                        {companies.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-0.5">Marca (moto)</label>
                      <Select
                        value={clientFilters.marca}
                        onValueChange={(v) =>
                          setClientFilters((f) => ({ ...f, marca: v, modelo: '' }))
                        }
                        placeholder="Todas"
                      >
                        <option value="">Todas</option>
                        {marcas.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-0.5">Modelo (moto)</label>
                      <Select
                        value={clientFilters.modelo}
                        onValueChange={(v) => setClientFilters((f) => ({ ...f, modelo: v }))}
                        placeholder="Todos"
                      >
                        <option value="">Todos</option>
                        {modelos.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="flex-1 grid grid-cols-2 gap-1">
                        <div>
                          <label className="text-xs text-muted-foreground block mb-0.5">Tít. aberto mín.</label>
                          <Input
                            type="number"
                            min={0}
                            value={clientFilters.minTitulosAberto}
                            onChange={(e) => setClientFilters((f) => ({ ...f, minTitulosAberto: e.target.value }))}
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground block mb-0.5">máx.</label>
                          <Input
                            type="number"
                            min={0}
                            value={clientFilters.maxTitulosAberto}
                            onChange={(e) => setClientFilters((f) => ({ ...f, maxTitulosAberto: e.target.value }))}
                            placeholder="—"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="flex-1 grid grid-cols-2 gap-1">
                        <div>
                          <label className="text-xs text-muted-foreground block mb-0.5">Locação mín. (meses)</label>
                          <Input
                            type="number"
                            min={0}
                            value={clientFilters.minMesesLocacao}
                            onChange={(e) => setClientFilters((f) => ({ ...f, minMesesLocacao: e.target.value }))}
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground block mb-0.5">máx. (meses)</label>
                          <Input
                            type="number"
                            min={0}
                            value={clientFilters.maxMesesLocacao}
                            onChange={(e) => setClientFilters((f) => ({ ...f, maxMesesLocacao: e.target.value }))}
                            placeholder="—"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-end">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setClientFilters({
                            companyId: '',
                            marca: '',
                            modelo: '',
                            minTitulosAberto: '',
                            maxTitulosAberto: '',
                            minMesesLocacao: '',
                            maxMesesLocacao: '',
                          })
                        }
                      >
                        Limpar filtros
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <label className="text-sm font-medium">Clientes (múltipla escolha)</label>
                    <span className="text-xs text-muted-foreground">
                      {loadingClientes ? 'Carregando...' : `${clientesFiltered.length} cliente(s)`}
                    </span>
                  </div>
                  <Input
                    type="text"
                    placeholder="Buscar por nome ou código"
                    value={clientNameSearch}
                    onChange={(e) => setClientNameSearch(e.target.value)}
                    className="mb-2"
                  />
                  <div className="flex gap-2 mb-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const ids = clientesFiltered.map((c) => c.id);
                        setSendForm((prev) => ({
                          ...prev,
                          clienteFornecedorIds: Array.from(new Set([...(prev.clienteFornecedorIds ?? []), ...ids])),
                        }));
                      }}
                      disabled={clientesFiltered.length === 0}
                    >
                      Selecionar todos os filtrados
                    </Button>
                  </div>
                  <div className="border rounded-md p-2 max-h-48 overflow-y-auto space-y-1">
                    {loadingClientes ? (
                      <p className="text-sm text-muted-foreground">Carregando clientes...</p>
                    ) : (
                      clientesFiltered.slice(0, 500).map((c) => (
                        <label key={c.id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={sendForm.clienteFornecedorIds?.includes(c.id) ?? false}
                            onChange={() => toggleCliente(c.id)}
                          />
                          <span className="text-sm truncate">{c.nome} ({c.codigo})</span>
                        </label>
                      ))
                    )}
                    {!loadingClientes && clientesFiltered.length > 500 && (
                      <p className="text-xs text-muted-foreground">Exibindo os primeiros 500.</p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Empresas / Grupos (múltipla escolha)</label>
                  <div className="border rounded-md p-2 max-h-48 overflow-y-auto space-y-1">
                    {companies.map((c) => (
                      <label key={c.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={sendForm.companyIds?.includes(c.id) ?? false}
                          onChange={() => toggleCompany(c.id)}
                        />
                        <span className="text-sm truncate">{c.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleSendPush} disabled={sending}>
                  <Send className="h-4 w-4 mr-2" />
                  {sending ? 'Enviando...' : 'Enviar push'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Modal
        isOpen={templateModalOpen}
        onClose={() => {
          setTemplateModalOpen(false);
          setEditingTemplate(null);
          resetTemplateForm();
        }}
        title={editingTemplate ? 'Editar template' : 'Novo template'}
        size="lg"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Nome</label>
            <Input
              value={templateForm.name}
              onChange={(e) => setTemplateForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Ex: Lembrete de vencimento"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Categoria</label>
            <Select
              value={templateForm.category}
              onValueChange={(v) => setTemplateForm((prev) => ({ ...prev, category: v }))}
              placeholder="Categoria"
            >
              {Object.entries(PUSH_TEMPLATE_CATEGORY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Título do push</label>
            <Input
              value={templateForm.title}
              onChange={(e) => setTemplateForm((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Título exibido no celular"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Corpo da mensagem</label>
            <Textarea
              value={templateForm.body}
              onChange={(e) => setTemplateForm((prev) => ({ ...prev, body: e.target.value }))}
              placeholder="Texto da notificação"
              rows={4}
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Empresa (opcional)</label>
            <Select
              value={templateForm.companyId}
              onValueChange={(v) => setTemplateForm((prev) => ({ ...prev, companyId: v }))}
              placeholder="Template global"
            >
              <option value="">Global</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="template-active"
              checked={templateForm.isActive}
              onChange={(e) => setTemplateForm((prev) => ({ ...prev, isActive: e.target.checked }))}
            />
            <label htmlFor="template-active">Ativo</label>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setTemplateModalOpen(false);
                setEditingTemplate(null);
                resetTemplateForm();
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveTemplate}
              disabled={createTemplateMutation.isPending || updateTemplateMutation.isPending}
            >
              {editingTemplate ? 'Salvar' : 'Criar'}
            </Button>
          </div>
          </div>

          {/* Pré-visualização no celular */}
          <div className="flex flex-col items-center lg:items-start">
            <p className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
              <Smartphone className="h-4 w-4" />
              Como será exibido no celular
            </p>
            <div className="w-full max-w-[280px] rounded-[2rem] border-4 border-neutral-800 bg-neutral-900 p-3 shadow-xl">
              <div className="rounded-[1.5rem] bg-neutral-950 min-h-[320px] flex flex-col items-center pt-6">
                <div className="w-24 h-1.5 rounded-full bg-neutral-700 mb-6" />
                <div className="w-[calc(100%-16px)] rounded-xl bg-neutral-800/95 border border-neutral-700/80 shadow-lg overflow-hidden text-left">
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-neutral-700/60">
                    <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
                      <span className="text-primary text-xs font-bold">Z</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-medium text-neutral-300 truncate">Zehmotoca</p>
                      <p className="text-[9px] text-neutral-500">Agora</p>
                    </div>
                  </div>
                  <div className="px-3 py-2.5 space-y-0.5">
                    <p className="text-xs font-semibold text-white leading-tight line-clamp-1">
                      {templateForm.title.trim() || 'Título da notificação'}
                    </p>
                    <p className="text-[11px] text-neutral-400 leading-snug line-clamp-3">
                      {templateForm.body.trim() || 'Corpo da mensagem aparecerá aqui.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};
